import { useEffect, useState } from "react";
import { CheckCircle2, Clock, Loader2, ShieldAlert, XCircle } from "lucide-react";
import {
  explainFlags,
  fetchReferralTimeline,
  STATUS_LABELS,
  type ReferralTimelineRow,
} from "@/lib/referralReview";

const ICONS: Record<string, typeof Clock> = {
  pending: Clock,
  completed: CheckCircle2,
  held_for_review: ShieldAlert,
  void: XCircle,
};

/** Referral claim timeline with the exact reason behind any fraud hold. */
export function ReferralTimeline() {
  const [rows, setRows] = useState<ReferralTimelineRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReferralTimeline()
      .then(setRows)
      .catch((e) => setError((e as Error).message));
  }, []);

  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {error}
      </p>
    );
  }

  if (!rows) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading referral timeline…
      </p>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No referral claims yet. Share your link — each claim shows up here with its status.
      </p>
    );
  }

  return (
    <ol className="space-y-3">
      {rows.map((r) => {
        const Icon = ICONS[r.status] ?? Clock;
        const held = r.status === "held_for_review" || r.review_status === "review";
        return (
          <li
            key={r.referral_id}
            className="rounded-lg border border-border bg-secondary/20 p-3"
          >
            <div className="flex items-start gap-3">
              <Icon
                className={`size-4 mt-0.5 shrink-0 ${
                  r.status === "completed"
                    ? "text-primary"
                    : held
                      ? "text-amber-500"
                      : "text-muted-foreground"
                }`}
                aria-hidden
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold">
                    {STATUS_LABELS[r.status] ?? r.status}
                  </span>
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()}
                  </span>
                  {typeof r.reward_credits === "number" && r.reward_credits > 0 && (
                    <span className="font-mono text-[10px] text-primary">
                      +{r.reward_credits} credits
                    </span>
                  )}
                </div>
                {held && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {r.hold_reason ??
                      "This claim is waiting on a manual fraud review before credits are released."}
                  </p>
                )}
                {held && explainFlags(r.risk_flags).length > 0 && (
                  <ul className="mt-1 list-disc pl-5 text-xs text-muted-foreground">
                    {explainFlags(r.risk_flags).map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                )}
                {r.status === "pending" && !held && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Waiting for your friend to complete their purchase.
                  </p>
                )}
                {r.status === "completed" && r.completed_at && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Approved {new Date(r.completed_at).toLocaleDateString()}
                    {typeof r.validated_amount_cents === "number" && r.validated_amount_cents > 0
                      ? ` · validated purchase $${(r.validated_amount_cents / 100).toFixed(0)}`
                      : ""}
                  </p>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
